/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/components/SideTip.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useEffect, useState, type RefObject } from 'react';

/**
 * Collapsed sidebar: the label of the hovered or focused `[data-tip]` item, floated to its right.
 * Fixed and outside the sidebar, because the sidebar scrolls and would clip a CSS tooltip.
 */
export function SideTip({ root, enabled }: { root: RefObject<HTMLElement | null>; enabled: boolean }) {
  const [tip, setTip] = useState<{ text: string; top: number; left: number } | null>(null);
  useEffect(() => {
    const el = root.current;
    setTip(null);
    if (!el || !enabled) return;
    const show = (e: Event) => {
      if (!matchMedia('(min-width: 901px)').matches) return; // below that the sidebar is the full-width drawer
      const t = (e.target as HTMLElement).closest<HTMLElement>('[data-tip]');
      if (!t || !el.contains(t)) { setTip(null); return; }
      const r = t.getBoundingClientRect();
      setTip({ text: t.dataset.tip!, top: r.top + r.height / 2, left: r.right + 10 });
    };
    const hide = () => setTip(null);
    el.addEventListener('mouseover', show); el.addEventListener('focusin', show);
    el.addEventListener('mouseleave', hide); el.addEventListener('focusout', hide); el.addEventListener('scroll', hide);
    return () => {
      el.removeEventListener('mouseover', show); el.removeEventListener('focusin', show);
      el.removeEventListener('mouseleave', hide); el.removeEventListener('focusout', hide); el.removeEventListener('scroll', hide);
    };
  }, [root, enabled]);
  return tip ? <div className="side-tip" role="tooltip" style={{ top: tip.top, left: tip.left }}>{tip.text}</div> : null;
}
